import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./emptyCell.css";

/** Inline illustration when remote empty-state art fails or URL is missing */
function EmptyStateIllustrationFallback({ size = 96 }) {
  return (
    <svg
      className="emptyCell-fallback-svg"
      width={size}
      height={size}
      viewBox="0 0 96 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
    >
      <rect
        x="26"
        y="28"
        width="44"
        height="52"
        rx="4"
        stroke="rgba(255, 255, 255, 0.35)"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M26 38h44"
        stroke="rgba(255, 255, 255, 0.2)"
        strokeWidth="2"
      />
      <rect x="32" y="46" width="24" height="3" rx="1.5" fill="rgba(255, 255, 255, 0.35)" />
      <rect x="32" y="54" width="18" height="3" rx="1.5" fill="rgba(255, 255, 255, 0.22)" />
      <rect x="32" y="62" width="20" height="3" rx="1.5" fill="rgba(255, 255, 255, 0.22)" />
      <circle cx="64" cy="50" r="14" fill="none" stroke="#F94D1F" strokeOpacity="0.85" strokeWidth="1.8" />
      <path
        d="M64 44v12M58 50h12"
        stroke="#F94D1F"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * A component that renders an empty cell with customizable content.
 * @param {Object} props - The props object containing the following properties:
 *   - clearSearchButton: A function to handle the clear search button click event.
 *   - noDataTitle: The title of the empty cell.
 *   - noDataDesc: The description of the empty cell.
 *   - emptyItems: The number of empty items in the cell.
 *   - noDataAddButton: A boolean indicating whether to show the add button.
 *   - noDataAddButtonTitle: The title of the add button.
 *   - emptyDataAddButton: A function to handle the empty data add button click event.
 * @returns The rendered empty
 */
const EmptyCell = (props) => {
  const { t } = useTranslation();

  /**
   * Destructures the properties object and assigns the values to individual variables.
   * @param {object} props - The properties object containing the values to destructure.
   * @returns None
   */
  const {
    clearSearchButton,
    noDataTitle,
    noDataDesc,
    emptyItems,
    noDataAddButton,
    noDataAddButtonTitle,
    emptyDataAddButton,
    emptyImg,
    emptyTitle,
    emptyDataDesc,
    emptyDataDescTow,
    noDataDescTwo
  } = props;
  const emptyItemsLength = emptyItems;

  const noDataRef = useRef(null); // Create a ref for the .NoDataFoound element
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [emptyImg]);

  useEffect(() => {
    // Get the .NoDataFoound element using the ref
    const noDataElement = noDataRef.current;

    // Get the nearest <tr> ancestor element
    const nearestTr = noDataElement.closest("tr");

    // Add a class to the nearest <tr> element
    if (nearestTr) {
      nearestTr.classList.add("emptyRow"); // Replace "custom-class" with your desired class name
    }
  }, []);

  return (
    <div ref={noDataRef} className="NoDataFoound">
      <div className="NoData_Icon">
        {emptyItemsLength?.length === 0 ? (
          !emptyImg || imageFailed ? (
            <EmptyStateIllustrationFallback size={96} />
          ) : (
            <img
              src={emptyImg}
              alt=""
              role="presentation"
              width={96}
              height={96}
              decoding="async"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          )
        ) : (
          <span className="Icon">
            <i className="bx bx bx-search bx-xs" />
          </span>
        )}
        {emptyItemsLength?.length === 0 ? (
          <span className="No-Data-text">{emptyTitle}</span>
        ) : (
          <span className="No-Data-text">{noDataTitle}</span>
        )}
        {emptyItemsLength?.length === 0 ? (
          <span className="No-Desc Empty-Deask">{emptyDataDesc}<br /> {emptyDataDescTow}</span>
        ) : (
          <span className="No-Desc">{noDataDesc}<br /> {noDataDescTwo} </span>
        )}
        <div
          className={
            emptyItemsLength?.length === 0
              ? "noDataBtns emptyDataBtns"
              : "noDataBtns"
          }
        >
          <button className="fillButton clearBtnStyle" onClick={clearSearchButton}>
            {t("CLEAR_SEARCH")}
          </button>
          {noDataAddButton && (
            <button className="fillButton addBtnStyle" onClick={emptyDataAddButton}>
              <i className="bx bxs-plus-circle"></i>
              {noDataAddButtonTitle}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmptyCell;
